import type { CSSProperties } from 'react';
import { Fragment } from 'react';

import type { A11y, Color, DividerSpacing, DividerTheme } from '@snowit/icetypes';

import { COLOR } from '@snowit/icetypes';
import testId from '@snowit/utils/testId';
import validators from '@snowit/utils/validators';

import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Divider.module.scss';

export interface IDivider {
  theme?: DividerTheme;
  spacing?: DividerSpacing; // Top and bottom padding in REM
  label?: string;
  lineColor?: Color | (string & {});
  classNames?: { wrapper?: string; border?: string; label?: string };
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

const Divider = (props: IDivider) => {
  const { theme = 'line', spacing = [1.5], label, lineColor, classNames, test, a11y } = props;

  const resolvedLineColor = COLOR.includes(lineColor as Color)
    ? `var(--c-${lineColor})`
    : lineColor;

  const _spacing = (Array.isArray(spacing) ? spacing : [spacing]).filter((sp): sp is number =>
    validators.isNumber(sp),
  );

  const renderLine = () => {
    if (theme === 'text' || theme === 'space') return null;
    return <div className={cx(s.border, classNames?.border)} />;
  };

  const renderText = () => {
    if ((theme !== 'text' && theme === 'space') || !label) return null;

    return (
      <Fragment>
        <div className={cx(s.border, classNames?.border)} />
        <Typo className={cx(s.label, classNames?.label)} text={label} size={'sm'} color={'bg-d4'} />
        <div className={cx(s.border, classNames?.border)} />
      </Fragment>
    );
  };

  return (
    <div
      className={cx(s.wrapper, classNames?.wrapper, { [s[theme]]: true })}
      style={
        {
          '--lm': _spacing
            .map((value) => `${value}rem`)
            .filter(Boolean)
            .join(' '),
          '--c-border': resolvedLineColor,
        } as CSSProperties
      }
      role={a11y?.role || 'separator'}
      tabIndex={-1}
      {...testId(test)}
    >
      {renderLine()}
      {renderText()}
    </div>
  );
};

export default Divider;
